import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { createPortal } from 'react-dom';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    className?: string;
    variant?: 'center' | 'bottom';
    glass?: boolean;
}

export function Modal({ isOpen, onClose, children, title, className, variant = 'center', glass }: ModalProps) {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const variants = {
        center: {
            initial: { opacity: 0, scale: 0.95, y: 20 },
            animate: { opacity: 1, scale: 1, y: 0 },
            exit: { opacity: 0, scale: 0.95, y: 20 }
        },
        bottom: {
            initial: { opacity: 0, y: "100%" },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: "100%" }
        }
    };

    const baseClasses = variant === 'center'
        ? "fixed inset-x-4 top-[10%] bottom-[10%] z-50 flex flex-col pointer-events-none md:max-w-md md:mx-auto md:top-[15%] md:bottom-[15%]"
        : "fixed inset-x-0 bottom-0 z-50 flex flex-col pointer-events-none md:max-w-md md:mx-auto h-[80vh] md:h-[85vh] md:bottom-0 md:rounded-t-[30px]";

    const contentClasses = glass
        ? "glass-strong w-full h-full rounded-[30px] shadow-2xl overflow-hidden flex flex-col pointer-events-auto border border-white/10"
        : variant === 'center'
            ? "bg-[#1c1c1e] w-full h-full rounded-[30px] shadow-2xl overflow-hidden flex flex-col pointer-events-auto border border-white/5"
            : "bg-[#1c1c1e] w-full h-full rounded-t-[30px] shadow-2xl overflow-hidden flex flex-col pointer-events-auto border-t border-x border-white/5";

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={variants[variant].initial}
                        animate={variants[variant].animate}
                        exit={variants[variant].exit}
                        transition={{ type: "spring", duration: 0.5, bounce: 0 }}
                        className={cn(baseClasses, className)}
                    >
                        <div className={contentClasses}>
                            {/* Header */}
                            <div className={`flex items-center justify-between px-5 py-4 border-b border-white/5 z-10 ${glass ? 'bg-transparent' : 'bg-[#1c1c1e]'}`}>
                                <h2 className="text-[17px] font-semibold text-white">{title}</h2>
                                <button
                                    onClick={onClose}
                                    className="w-8 h-8 rounded-full bg-[#2c2c2e] flex items-center justify-center hover:bg-[#3c3c3e] transition-colors"
                                >
                                    <X className="w-4 h-4 text-white/60" />
                                </button>
                            </div>

                            {/* Scrollable Body */}
                            <div className="flex-1 overflow-y-auto p-5 no-scrollbar">
                                {children}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
}
